import { PluginItem } from "@babel/core";
import {
  autorun,
  computed,
  IObservableValue,
  observable,
  runInAction,
  untracked,
} from "mobx";
import { atom } from "../atom";
import {
  EvaluateExpressionType,
  LoadState,
  Pane,
  PaneInput,
  PaneOutput,
  PaneType,
  SupportedBabelPlugin,
} from "../Types";
import {
  activePaneStates,
  canvasAtom,
  globalValuesMobx,
  shouldClonePaneInputsBox,
} from "./CanvasState";
import { babelLogInspectorPlugin } from "./LogInspector";
import { getPaneIdFromPaneInput, isEmptyEvalExpression } from "./SandboxUtils";

const AsyncFunction = Object.getPrototypeOf(async function () {}).constructor;

const babelAtom = atom<
  LoadState<typeof import("../lib/babel.min")> | undefined
>(undefined);
const loadedBabelPluginsAtom = observable<{
  [pluginUrl: string]: LoadState<any>;
}>({});

function loadBabel() {
  if (babelAtom.value !== undefined) {
    return;
  }
  runInAction(() => {
    // @ts-ignore
    babelAtom.value = ["loading"];
  });
  import("../lib/babel.min").then((babel) => {
    runInAction(() => {
      // @ts-ignore
      babelAtom.value = ["value", babel];
    });
  });
}
function identity<T>(v: T): T {
  return v;
}

export function getPaneInputBox(
  paneInput: PaneInput
): IObservableValue<PaneOutput> | undefined {
  if (paneInput.source === undefined) {
    return undefined;
  }
  const inputPaneId = getPaneIdFromPaneInput(paneInput)!;
  const activePaneState = activePaneStates.get(inputPaneId);
  if (activePaneState === undefined) {
    return undefined;
  }
  return activePaneState.outputBoxes[paneInput.source[1]];
}

function calculateGlobalNamesForPanes(panes: Pane[]): string[] {
  return panes
    .map((pane) => {
      if (
        pane.type === PaneType.EvaluateGlobal ||
        (pane.type === PaneType.Import && pane.isGlobal)
      ) {
        return pane.name;
      }
      return undefined;
    })
    .filter((v): v is string => v !== undefined);
}
export const hasLoadedGlobalsComputed = computed(() => {
  const globalNames = calculateGlobalNamesForPanes(canvasAtom.value.panes);
  return globalNames.every((globalName) => globalName in globalValuesMobx);
});

function getTransformCodeComputed(babelPluginIdsGet: {
  get(): (SupportedBabelPlugin | "log-inspector")[];
}) {
  return computed(
    (): LoadState<(code: string, prefixLength: number) => string> => {
      const babelPluginIds = babelPluginIdsGet.get();
      if (babelPluginIds.length === 0) {
        return ["value", identity];
      }
      const babel = babelAtom.value;
      if (babel === undefined || babel[0] === "loading") {
        return ["loading", "babel"];
      }
      if (babel[0] === "error") {
        return ["error", "error loading babel"];
      }

      const pluginItems: PluginItem[] = [];
      let loadingPlugin: string | undefined;
      let useBabelLogInspectorPlugin = false;
      for (const babelPluginId of babelPluginIds) {
        if (babelPluginId === "log-inspector") {
          useBabelLogInspectorPlugin = true;
        } else if (typeof babelPluginId === "string") {
          pluginItems.push(babelPluginId);
        } else {
          const [, url] = babelPluginId;
          const babelPlugin = loadedBabelPluginsAtom[url];
          if (babelPlugin === undefined || babelPlugin[0] === "loading") {
            loadingPlugin = url;
          } else if (babelPlugin[0] === "error") {
            return ["error", `plugin ${babelPlugin}`];
          } else {
            pluginItems.push(babelPlugin[1]);
          }
        }
      }
      if (loadingPlugin !== undefined) {
        return ["loading", loadingPlugin];
      }

      return [
        "value",
        (code, prefixLength) => {
          return babel[1].transform(code, {
            plugins: useBabelLogInspectorPlugin
              ? [...pluginItems, [babelLogInspectorPlugin, { prefixLength }]]
              : pluginItems,
            parserOpts: {
              allowReturnOutsideFunction: true,
              allowAwaitOutsideFunction: true,
            },
          }).code!;
        },
      ];
    }
  );
}

function computeFunction(
  expression: string,
  expressionType: EvaluateExpressionType,
  transformCode: LoadState<(code: string, prefixLength: number) => string>,
  inputs: { name?: string }[],
  globalValueNames: string[],
  extraArgNames: string[],
  sourceMapId: string | undefined
): LoadState<Function> {
  if (expressionType === EvaluateExpressionType.Text) {
    return ["value", async () => expression];
  }
  if (transformCode[0] === "loading") {
    return transformCode;
  } else if (transformCode[0] === "error") {
    return ["error", transformCode[1]];
  }
  try {
    const args = [
      "inputs",
      ...inputs.map((input) => input.name).filter(Boolean),
      ...extraArgNames,
      ...globalValueNames,
    ];
    const prefix =
      expressionType === EvaluateExpressionType.FunctionBody
        ? ""
        : `return ${!isEmptyEvalExpression(expression) ? "(" : ""}`;
    args.push(
      `"use strict";\n${transformCode[1](
        `${prefix}${expression}${prefix.endsWith("(") ? ")" : ""}`,
        prefix.length
      )}\n${sourceMapId !== undefined ? `//# sourceURL=${sourceMapId}` : ""}`
    );
    return ["value", new AsyncFunction(...args)];
  } catch (e: any) {
    return ["error", e.message];
  }
}

export function getFunctionComputed<InputType extends { name?: string }>(
  isGlobal: boolean,
  expressionTypeGet: { get(): EvaluateExpressionType },
  expressionGet: { get(): string },
  paneInputsGet: { get(): InputType[] },
  babelPluginIdsGet: {
    get(): (SupportedBabelPlugin | "log-inspector")[];
  },
  extraArgNames: string[],
  sourceMapId?: string | undefined
) {
  const transformCodeComputed = getTransformCodeComputed(babelPluginIdsGet);
  return computed(() =>
    computeFunction(
      expressionGet.get(),
      expressionTypeGet.get(),
      transformCodeComputed.get(),
      paneInputsGet.get(),
      isGlobal ? [] : Object.keys(globalValuesMobx),
      extraArgNames,
      sourceMapId
    )
  );
}

export class Evaluator<InputType extends { name?: string }> {
  runId = 0;

  constructor(
    readonly functionGet: { get(): LoadState<Function> },
    readonly outputBox: IObservableValue<PaneOutput>,
    readonly isGlobal: boolean,
    readonly paneInputsGet: { get(): InputType[] },
    readonly getOutputBoxForInput: (
      input: InputType
    ) => IObservableValue<PaneOutput> | undefined,
    readonly babelPluginIdsGet: {
      get(): (SupportedBabelPlugin | "log-inspector")[];
    },
    readonly autorunGet: { get(): boolean },
    readonly isTryCatchDisabledGet: { get(): boolean } | undefined,
    readonly extraArgs: Record<string, (runId: number) => any>,
    readonly beforeRun?: (runId: number) => void,
    readonly afterRun?: (runId: number) => void
  ) {}

  init() {
    if (this.outputBox.get()[0] !== "waiting") {
      runInAction(() => {
        this.outputBox.set(["waiting"]);
      });
    }
    const unsubscribe = [
      autorun(() => {
        const babelPluginIds = this.babelPluginIdsGet.get();
        if (babelPluginIds.length > 0) {
          loadBabel();

          for (const babelPluginId of babelPluginIds) {
            if (Array.isArray(babelPluginId)) {
              const [, url] = babelPluginId;
              if (loadedBabelPluginsAtom[url] === undefined) {
                loadedBabelPluginsAtom[url] = ["loading"];
                import(/* @vite-ignore */ url)
                  .then((m) => {
                    loadedBabelPluginsAtom[url] = ["value", m.default];
                  })
                  .catch(() => {
                    loadedBabelPluginsAtom[url] = ["error"];
                  });
              }
            }
          }
        }
      }),
      autorun(() => {
        if (!this.autorunGet.get()) {
          return;
        }
        this.run();
      }),
    ];
    return () => {
      unsubscribe.forEach((unsubscribe) => unsubscribe());
    };
  }

  async compute(): Promise<PaneOutput> {
    const f = this.functionGet.get();

    const paneInputs = this.paneInputsGet.get();
    if (f[0] === "error") {
      return ["error", f[1]];
    }
    if (f[0] !== "value") {
      return ["waiting"];
    }
    const inputs: any[] = [];
    const namedInputs: any[] = [];
    for (const paneInput of paneInputs) {
      const inputAtom = this.getOutputBoxForInput(paneInput);
      if (inputAtom?.get()[0] !== "value") {
        return ["waiting", "input"];
      }
      let inputValue = inputAtom.get()[1];
      if (shouldClonePaneInputsBox.get()) {
        try {
          inputValue = structuredClone(inputValue);
        } catch {}
      }
      inputs.push(inputValue);
      if (paneInput.name !== undefined) {
        namedInputs.push(inputValue);
      }
    }

    const go = async (): Promise<PaneOutput> => {
      const rv = f[1](
        inputs,
        ...namedInputs,
        ...Object.values(this.extraArgs).map((f) => f(this.runId)),
        ...(this.isGlobal ? [] : Object.values(globalValuesMobx))
      );
      let didComplete = false;
      const [promiseValue] = await Promise.all([
        rv.then((v: any) => {
          // setting this in finally is too slow
          didComplete = true;
          return v;
        }),
        Promise.resolve().then(() => {
          if (!didComplete) {
            runInAction(() => {
              this.outputBox.set(["running"]);
            });
          }
        }),
      ]);
      return ["value", promiseValue];
    };
    const isTryCatchDisabled = this.isTryCatchDisabledGet?.get() ?? false;
    if (isTryCatchDisabled) {
      const value = await go();
      return value;
    } else {
      try {
        const value = await go();
        return value;
      } catch (e) {
        return ["error", e];
      }
    }
  }

  async run() {
    this.runId++;
    const runId = this.runId;
    untracked(() => {
      this.beforeRun?.(runId);
    });
    const value = await this.compute();
    if (this.runId === runId) {
      runInAction(() => {
        this.outputBox.set(value);
      });
      untracked(() => {
        this.afterRun?.(runId);
      });
    }
  }
}
